import { TBundleItem, TItem, TSessionItem } from '@utils/filter';
import {
  TBundle,
  TSession,
} from '@fe-learner-common/api/schemas/sessionsSchema';
import { useSessions } from '@fe-learner-common/hooks/data/useSessions';
import { useMemo } from 'react';
export type TSessionsData = {
  items: TItem[];
  sessionMap: Map<number, TSession>;
};
const mapSessionsData = ({
  sessions,
  bundles,
}: {
  sessions: TSession[];
  bundles: TBundle[];
}): TSessionsData => {
  const sessionMap = new Map(sessions.map((session) => [session.id, session]));

  const bundleSessions = new Set(bundles.flatMap((bundle) => bundle.sessions));
  const sessionNotInBundle: TSessionItem[] = sessions
    .filter((session) => !bundleSessions.has(session.id))
    .map((session) => ({ ...session, type: 'session' }));

  const bundleItems: TBundleItem[] = bundles.map((bundle) => ({
    ...bundle,
    type: 'bundle',
  }));

  const items = [...sessionNotInBundle, ...bundleItems];

  return { items, sessionMap };
};

export const getBundleSessions = (
  bundle: TBundle,
  sessions?: Map<number, TSession>,
): TSession[] | null => {
  return bundle.sessions.reduce((acc, sessionId) => {
    const session = sessions?.get(sessionId);
    if (session) {
      acc = [...acc, session];
    }
    return acc;
  }, [] as TSession[]);
};

export const useSessionsData = () => {
  const {
    data: sessionResponse,
    isLoading,
    isFetching,
    isError,
    error,
  } = useSessions({ enabled: true });

  const sessionData: TSessionsData | null = useMemo(() => {
    if (!sessionResponse) {
      return null;
    }
    const { bundles, sessions } = sessionResponse;

    return mapSessionsData({ sessions, bundles });
  }, [sessionResponse]);

  return { sessionData, isLoading, isFetching, isError, error };
};
